import * as React from "react";
import { useEffect, useState } from "react";
import "./AboutPanel.scss";
import LoadingSpinner from "../flow/utils/LoadingSpinner";
import sfetch from "../commons/utils/super-fetch";
import { PatientInformation, Tag, TagActions } from "../commons/model/patient/PatientInfo";
import PatientTagSetter, { TagModalInfo } from "./PatientTagSetter";
import TagModal from "../settings/tags/TagModal";
import { ModalType, TagModalStates } from "../settings/tags/TagModalTypeAndState";
import { Danger } from "../commons/components/Alerts";
import { dateWithOptionalTime } from "../commons/utils/date";

interface AboutProps {
  leadInformation: PatientInformation;
  tagActions: TagActions;
}

export default function AboutPanel({ leadInformation, tagActions }: AboutProps) {
  const [loading, setLoading] = useState(true);
  const [leadTags, setLeadTags] = useState<Tag[]>();
  const [availableTags, setAvailableTags] = useState<Tag[]>();
  const [displayAddLabelModal, setDisplayAddLabelModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<{
    display: boolean;
    content?: string;
  }>({ display: false });
  const [currentFilter, setCurrentFilter] = useState<string>("");

  useEffect(() => {
    loadLeadTags();
    loadAvailableTags();
  }, []);

  useEffect(() => {
    if (leadTags && availableTags) {
      setLoading(false);
    }
  }, [leadTags, availableTags]);

  const loadLeadTags = () => {
    sfetch(tagActions.leadTagsUrl, {
      method: "GET",
    })
      .then((response) => response.json())
      .then((response) => {
        setLeadTags(response);
      });
  };

  const loadAvailableTags = () => {
    sfetch(tagActions.availableTagsUrl, {
      method: "GET",
    })
      .then((response) => response.json())
      .then((response) => {
        setAvailableTags(response);
      });
  };

  const onTagAdd = (tagModalInfo: TagModalInfo) => {
    setDisplayAddLabelModal(tagModalInfo.displayModal);
    setCurrentFilter(tagModalInfo.currentFilter);
  };

  const onTagChange = () => {
    reloadTags();
  };

  const onTagAdded = (tag: Tag, patientInformation: PatientInformation) => {
    setErrorMessage({ display: false });
    const url = tagActions.addTagToPatientUrl.replace("uid", patientInformation.contactInfoUid);
    return sfetch(url, {
      method: "PUT",
      body: JSON.stringify({
        tagUid: tag.uid,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        reloadTags();
      })
      .catch(() => {
        setErrorMessage({
          display: true,
          content: i18n["Page.Settings.Tags.ErrorMessage.Add.WithPatient"],
        });
      });
  };

  const reloadTags = () => {
    setLoading(true);
    loadLeadTags();
    loadAvailableTags();
    setCurrentFilter("");
  };

  const closeModal = (state: string) => {
    setErrorMessage({ display: false }); // reset success message animation
    setDisplayAddLabelModal(false);
    if (state === TagModalStates.ERROR) {
      setErrorMessage({
        display: true,
        content: i18n["Page.Settings.Tags.Modal.Add.ErrorMessage"],
      });
    }
  };

  return (
    (loading && <LoadingSpinner></LoadingSpinner>) || (
      <div className="v-flex with-gap about-panel">
        <div className={"react-panel-title fullWidth"}>
          <div className={"react-panel-title-container"}>
            {i18n["Page.PatientManagement.About.Title"].replace("{}", leadInformation.fullName)}
          </div>
        </div>
        <div className="about">
          <div className="inline-form-editor">
            <div className="input-form">
              <span className="input-name">{i18n["Page.PatientManagement.About.Creation"]}</span>
              <span className="input-value">
                {dateWithOptionalTime(leadInformation.creation, true)}
              </span>
              <span className="input-name tags-label">
                {i18n["Page.PatientManagement.About.Tags"]}
              </span>
              <span className="input-value tags-value">
                <PatientTagSetter
                  patientTags={leadTags}
                  availableTags={availableTags}
                  createTagUrl={tagActions.createTagUrl}
                  editUrl={tagActions.editTagsUrl}
                  onTagAdd={onTagAdd}
                  onChange={onTagChange}
                  patient={leadInformation}
                  displayModalHandler={displayAddLabelModal}
                  addButton={
                    <>
                      <i className="ic_plus" />
                      <div>{i18n["Page.Settings.Tags.Modal.Form.ConfirmButton.Add"]}</div>
                    </>
                  }
                />
              </span>
            </div>
          </div>
        </div>
        {errorMessage?.display && <Danger>{errorMessage.content}</Danger>}
        {displayAddLabelModal && (
          <TagModal
            modalType={ModalType.CREATE}
            closeModal={closeModal}
            fetchUrl={tagActions.createTagUrl}
            onTagAdded={onTagAdded}
            tagPrefilledValue={currentFilter}
            patient={leadInformation}
          />
        )}
      </div>
    )
  );
}
